/**
 * Sword parts in the game (lane SWORD): the figure's jian drawn from a part spec. Two hooks only:
 * - rig/sets.ts: `swordLayer(weapon, look) ?? weaponLayer(...)` — a one-handed jian is painted from its spec;
 *   any other weapon (sabre, staff, pairs) keeps the old drawing.
 * - sprites.ts (the outfit merge): `swordLook(o)` — a weapon item's look key puts its sword on the figure
 *   (`Look.sword`); with none, the default (catalog.ts `DEFAULT_SWORD`, direction C common).
 * The layer mirrors render/rig/weapons.ts `weaponLayer` (in hand, put away, sliding home) and adds carrying the
 * sword on the back (a spec's `scabbard.carry: 'back'`): the back line of a long weapon, turned hilt-up.
 */
import type { Look } from '../../looks/types';
import { stowPhase } from '../../motions/core';
import { flutter, type Layer, type Piece, zOf } from '../figure';
import type { Side, Skeleton } from '../skeleton';
import { stowAt, stowStep, weaponAim, type WeaponDesign } from '../weapons';
import { DEFAULT_SWORD } from './catalog';
import './look';
import { paintScabbard, paintSword, SWORD_BEHIND, swordLength } from './paint';
import { type SwordSpec, SWORDS } from './parts';

export { swordLook } from './look';

export const swordSpec = (id: string | undefined): SwordSpec => SWORDS.get(id ?? DEFAULT_SWORD) ?? SWORDS.get(DEFAULT_SWORD)!;

const FACING_DEPTH = { front: 1, front3: 0.55, side: 0, back3: -0.55, back: -1 } as const;
const DEPTH_PER_PX = 0.12;

function alongDepth(from: { x: number; y: number }, angle: number, fore: number, res: number, z0: number, perPx: number, max = 1.2) {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const ox = from.x * res;
  const oy = from.y * res;
  const f = res * Math.max(0.35, fore);
  return (x: number, y: number) => Math.max(-0.95, Math.min(max, z0 + (((x - ox) * dx + (y - oy) * dy) / f) * perPx));
}

/** The tassel's sway with the figure (fine px), still when standing square at ease. */
const swayOf = (sk: Skeleton, res: number) => (i: number, n: number) =>
  sk.square && sk.pose === 'idle' ? 0 : flutter(sk, i / n, i * 0.4, 0.6).dx * res * 0.5 * (i / n);

/** Where a sword is carried put away: the game's hip line, or the back line of a long weapon turned hilt-up. */
function carryLine(sk: Skeleton, w: WeaponDesign, side: Side, back: boolean) {
  if (!back) return stowAt(sk, w, side);
  const line = stowAt(sk, { ...w, kind: 'spear' }, side);
  const d = { x: Math.cos(line.angle), y: Math.sin(line.angle) };
  const mid = (w.ahead - w.behind) / 2;
  const half = (w.ahead + w.behind) / 2;
  const k = mid + half - w.behind - 1;
  return { at: { x: line.at.x + d.x * k, y: line.at.y + d.y * k }, angle: line.angle + Math.PI, behind: line.behind, hiltBehind: line.hiltBehind, scabbard: true };
}

/** The figure's sword layer for a one-handed jian (else nothing: the old weapon layer draws it). */
export function swordLayer(weapon: WeaponDesign, look: Pick<Look, 'sword' | 'sheathed'>): Layer | undefined {
  // `sword: 'off'` (outfit key `sword:off`): the old weapon drawing, for A/B checks.
  if (weapon.kind !== 'jian' || weapon.hands !== 'right' || look.sword === 'off') return undefined;
  const spec = swordSpec(look.sword);
  const sheathed = !!look.sheathed;
  const w: WeaponDesign = { ...weapon, ahead: swordLength(spec), behind: SWORD_BEHIND };
  const back = spec.scabbard.carry === 'back';
  return {
    id: 'weapon',
    label: 'อาวุธ',
    pieces(sk, fit) {
      const res = fit.res ?? 1;
      const side: Side = sk.rig.far === 'revArm' ? 'B' : 'A';
      const hand = sk.joints[`hand${side}`];
      const step = stowStep(sk, sheathed);
      const sway = swayOf(sk, res);
      const stow = carryLine(sk, w, side, back);
      // Worn at the hip head-on, the scabbard runs from its mouth over the robe back past the hip (weapons.ts).
      const scabbardDepth =
        !back && sk.square
          ? alongDepth(stow.at, stow.angle, 1, res, sk.back ? -0.1 : zOf(sk, 'chest', 0.07), -0.89 * FACING_DEPTH[sk.view] * DEPTH_PER_PX, 0.45)
          : undefined;
      const scabbardZ = back ? (stow.behind ? -0.9 : 0.9) : scabbardDepth ? (sk.back ? -0.1 : zOf(sk, 'chest', 0.07)) : stow.behind ? -0.9 : 0.9;
      const out: Piece[] = [
        { z: scabbardZ, ...(scabbardDepth ? { depth: scabbardDepth } : {}), draw: (g) => paintScabbard(g, spec, stow.at, stow.angle, res) },
      ];
      if (step.mode !== 'hand') {
        // Put away, or sliding home: the hilt (and as much of the blade as is still out) along the carry line.
        const k = step.mode === 'slide' ? step.k : 0;
        const d = { x: Math.cos(stow.angle), y: Math.sin(stow.angle) };
        const at = { x: stow.at.x - d.x * w.ahead * k, y: stow.at.y - d.y * w.ahead * k };
        out.push({
          z: stow.hiltBehind ? -0.9 : 0.9,
          draw: (g) => paintSword(g, spec, { x: at.x * res, y: at.y * res }, stow.angle, res, 1, 1 + w.ahead * k, sway),
        });
        return out;
      }
      // In hand: as weapons.ts lays it (under the gripping hand, its depth running along the blade).
      // Being put away or drawn: the blade turns from the guard toward its carry line (weapons.ts).
      const moving = stowPhase(sk.pose, sk.phase) !== null;
      const aim = weaponAim(moving ? { ...sk, rig: { ...sk.rig, arm: 'guard', armTo: undefined } } : sk, side);
      const turn = step.t ? Math.atan2(Math.sin(stow.angle - aim.angle), Math.cos(stow.angle - aim.angle)) * step.t : 0;
      const angle = aim.angle + turn;
      const overBack = sk.back && (sk.rig.armTo ?? sk.rig.arm) === 'reverseUp';
      const overArm = side === 'B' && sk.rig.far === 'revArm' && sk.square;
      const z0 = overBack ? 0.9 : overArm ? hand.z + 0.3 : aim.behind ? -0.8 : hand.z + (sk.back ? -0.005 : 0.06);
      out.push({
        z: z0,
        ...(aim.behind || overBack ? {} : { depth: alongDepth(hand, angle, aim.fore, res, z0, aim.ahead * FACING_DEPTH[sk.view] * DEPTH_PER_PX) }),
        draw: (g) => paintSword(g, spec, { x: hand.x * res, y: hand.y * res }, angle, res, aim.fore, undefined, sway),
      });
      return out;
    },
  };
}
